/**
 * Request deduplication service to prevent duplicate API calls
 */
interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestDeduplicationService {
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  /**
   * Deduplicate API requests by URL and method
   */
  async deduplicateRequest<T>(
    key: string,
    requestFn: () => Promise<T>,
    ttl: number = 5000 // 5 seconds default TTL
  ): Promise<T> {
    // Check if there's already a pending request
    const existing = this.pendingRequests.get(key);
    if (existing) {
      // Check if the request is still valid (not expired)
      if (Date.now() - existing.timestamp < this.REQUEST_TIMEOUT) {
        console.log(`Request deduplication: reusing pending request for ${key}`);
        return existing.promise;
      } else {
        // Remove expired request
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = requestFn().finally(() => {
      // Clean up after request completes
      setTimeout(() => {
        this.pendingRequests.delete(key);
      }, ttl);
    });

    // Store the pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });

    return promise;
  }

  /**
   * Generate a unique key for a request
   */
  generateKey(method: string, url: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramString}`;
  }

  /**
   * Clear all pending requests
   */
  clearAll(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get the number of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

// Global instance
export const requestDeduplication = new RequestDeduplicationService();

// Setup function for request deduplication
export const setupRequestDeduplication = () => {
  // The actual deduplication will be handled by individual components
  // using the requestDeduplication service directly
  // No setup needed for now
};
